import type { InstanceId, ItemId, ProductId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { Reward } from '@/core/content/schemas/progression';
import type { AcquireSourceKind, DomainEvent } from '@/core/events/types';
import type { LedgerEntry } from '@/core/persistence/db';
import type { GameSave } from '@/core/persistence/schema/v1';
import { grantUnlock } from './unlocks';

/**
 * Where coin rewards write their ledger rows while a command runs. Rewards are granted from deep inside
 * tasks, achievements, friendship levels, mail and mini games; the dispatcher opens this sink around each
 * command (synchronously) instead of every helper carrying the handler env.
 */
let ledgerSink: { rows: LedgerEntry[]; nowWallMs: number } | undefined;

export function withRewardLedger<T>(rows: LedgerEntry[] | undefined, nowWallMs: number, run: () => T): T {
  const outer = ledgerSink;
  ledgerSink = rows ? { rows, nowWallMs } : undefined;
  try {
    return run();
  } finally {
    ledgerSink = outer;
  }
}

const ledgerKind = (key: string): LedgerEntry['kind'] =>
  key.startsWith('mail:') ? 'mail' : key.startsWith('mg:') ? 'minigame' : 'reward';

/** Next runtime instance id: i_<seq base36> (content-schemas §1). */
export function newInstanceId(draft: GameSave): InstanceId {
  const id: InstanceId = `i_${draft.inventory.nextInstanceSeq.toString(36)}`;
  draft.inventory.nextInstanceSeq += 1;
  return id;
}

/** Add items to the bag. Products stack; everything else becomes one instance per unit. */
export function acquireItem(
  draft: GameSave,
  itemId: ItemId,
  qty: number,
  acquiredBy: AcquireSourceKind,
  ref?: string,
): DomainEvent {
  const instanceIds: InstanceId[] = [];
  if (itemId.startsWith('prod_')) {
    const pid = itemId as ProductId;
    draft.inventory.productStacks[pid] = (draft.inventory.productStacks[pid] ?? 0) + qty;
  } else {
    for (let i = 0; i < qty; i++) {
      const instanceId = newInstanceId(draft);
      draft.inventory.instances[instanceId] = {
        instanceId,
        itemId: itemId,
        acquiredBy,
        acquiredAtPlayMs: draft.clock.playTimeMs,
        location: { kind: 'bag' },
      };
      instanceIds.push(instanceId);
    }
  }
  return { type: 'ITEM_ACQUIRED', itemId, qty, instanceIds, acquiredBy, ...(ref ? { ref } : {}) };
}

function addToSet<T>(list: T[], value: T): void {
  if (!list.includes(value)) list.push(value);
}

/**
 * RewardService (content-schemas §14.8). `key` makes the grant idempotent: the same key never pays twice.
 * Stars are recorded per source key so `stars.total === Σ sources` always holds.
 */
export function grantRewards(
  draft: GameSave,
  content: ContentRegistry,
  key: string,
  rewards: readonly Reward[],
  acquiredBy: AcquireSourceKind = 'reward',
): DomainEvent[] {
  if (rewards.length === 0 || draft.rewardKeys.includes(key)) return [];
  draft.rewardKeys.push(key);
  const events: DomainEvent[] = [];
  for (const r of rewards) {
    switch (r.type) {
      case 'coins':
        draft.wallet.coins += r.amount;
        draft.shop.ledgerSummary.rewardsCoinsTotal += r.amount;
        // The key pays once per save, so save + key is a unique transaction id.
        ledgerSink?.rows.push({
          txId: `${draft.saveId}:${key}`,
          saveId: draft.saveId,
          atWallMs: ledgerSink.nowWallMs,
          atPlayMs: draft.clock.playTimeMs,
          kind: ledgerKind(key),
          coinsDelta: r.amount,
          balanceAfter: draft.wallet.coins,
          refs: { key },
        });
        break;
      case 'stars': {
        const prev = draft.stars.sources[key] ?? 0;
        draft.stars.sources[key] = prev + r.amount;
        draft.stars.total += r.amount;
        events.push({ type: 'STARS_CHANGED', delta: r.amount, total: draft.stars.total, sourceKey: key });
        break;
      }
      case 'item':
        if (content.items.has(r.itemId)) events.push(acquireItem(draft, r.itemId, r.qty, acquiredBy, key));
        break;
      case 'title':
        addToSet(draft.cosmetics.titles, r.titleId);
        break;
      case 'badge':
        addToSet(draft.cosmetics.badges, r.badgeId);
        break;
      case 'avatarPart':
        addToSet(draft.cosmetics.avatarParts, r.partId);
        break;
      case 'avatarFrame':
        addToSet(draft.cosmetics.avatarFrames, r.frameId);
        break;
      case 'sticker':
        addToSet(draft.cosmetics.stickers, r.stickerId);
        break;
      case 'photoFrame':
        addToSet(draft.cosmetics.photoFrames, r.frameId);
        break;
      case 'flag':
        draft.flags[r.flag] = true;
        break;
      case 'npcVisit': {
        // Only real once the neighbour has been met (content-schemas §14.11).
        const npc = (draft.npcs[r.npcId] ??= {
          friendship: 0,
          level: 0,
          talkCount: 0,
          lastDialogueId: null,
          pendingVisits: [],
          activeVisits: [],
        });
        const metFlag = content.npcs.get(r.npcId)?.metFlag;
        if (metFlag && draft.flags[metFlag]) addToSet(npc.activeVisits, r.roomId);
        else addToSet(npc.pendingVisits, r.roomId);
        break;
      }
      case 'unlock':
        events.push(...grantUnlock(draft, content, r.unlockId, false));
        break;
      case 'chapter':
        if (!draft.story.unlockedChapters.includes(r.chapterId)) {
          draft.story.unlockedChapters.push(r.chapterId);
          events.push({ type: 'CHAPTER_UNLOCKED', chapterId: r.chapterId });
        }
        break;
      case 'postcard': {
        if (key.startsWith('unlock:')) {
          // Postcards granted by an unlock arrive as a special letter in the mailbox (outside the 7-day cycle).
          const def = content.postcards.get(r.postcardId);
          const mailId = `mail_special_${r.postcardId.slice(3)}`;
          if (!draft.mailbox.special.some((m) => m.mailId === mailId)) {
            draft.mailbox.special.push({
              mailId,
              grantedForDayKey: draft.clock.lastSeenDayKey,
              cycleDay: 0,
              rewards: [{ ...r, isRepeat: false }, ...(def?.attachments ?? [])],
              kindForUi: 'special',
            });
          }
          break;
        }
        const entry = draft.story.postcards.find((p) => p.postcardId === r.postcardId);
        if (entry) entry.receivedCount += 1;
        else draft.story.postcards.push({ postcardId: r.postcardId, receivedCount: 1 });
        break;
      }
      case 'letter':
        addToSet(draft.story.letters, r.letterId);
        break;
      case 'shopPerk':
        addToSet(draft.shop.perks, r.perkId);
        break;
    }
  }
  events.push({ type: 'REWARD_GRANTED', key, rewards: [...rewards] });
  return events;
}
